/**
 * Playground API Service
 *
 * Handles HTTP requests for playground operations with error handling
 * and response transformation.
 *
 * Refactored to use top-level endpoints (no project_id) and Session-based model.
 */

import axios, { AxiosError } from 'axios';
import {
  PlaygroundSession,
  PlaygroundSessionCreate,
  PlaygroundSessionListResponse,
  PlaygroundSessionFilters,
  StepSequence,
  StepSequenceStep,
} from '../types/playground';

// Base URL from environment variable, default to localhost
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Playground Service - API client methods for playground operations
 */
export const playgroundService = {
  /**
   * Create a new playground session (top-level, no project_id)
   */
  createSession: async (data: PlaygroundSessionCreate): Promise<PlaygroundSession> => {
    try {
      const response = await apiClient.post<PlaygroundSession>(
        `/api/playground/runs`,
        data
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'playground session');
    }
  },

  /**
   * Get session history with optional search and pagination (top-level, no project_id)
   */
  getSessionHistory: async (filters?: PlaygroundSessionFilters): Promise<PlaygroundSessionListResponse> => {
    try {
      const params: Record<string, any> = {};

      if (filters?.limit !== undefined) params.limit = filters.limit;
      if (filters?.offset !== undefined) params.offset = filters.offset;
      if (filters?.search) params.search = filters.search;

      const response = await apiClient.get<PlaygroundSessionListResponse>(
        `/api/playground/runs`,
        { params }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'playground history');
    }
  },

  /**
   * Get full details of a specific session using existing session endpoint
   */
  getSessionDetails: async (sessionId: string): Promise<PlaygroundSession> => {
    try {
      const response = await apiClient.get<PlaygroundSession>(`/api/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'playground session');
    }
  },

  /**
   * Search sessions by message content (top-level, no project_id)
   */
  searchSessions: async (searchQuery: string, filters?: Omit<PlaygroundSessionFilters, 'search'>): Promise<PlaygroundSessionListResponse> => {
    try {
      const params: Record<string, any> = {
        search: searchQuery,
      };

      if (filters?.limit !== undefined) params.limit = filters.limit;
      if (filters?.offset !== undefined) params.offset = filters.offset;

      const response = await apiClient.get<PlaygroundSessionListResponse>(
        `/api/playground/runs`,
        { params }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'playground search');
    }
  },

  /**
   * Restart an existing playground session
   */
  restartSession: async (sessionId: string, data: PlaygroundSessionCreate): Promise<PlaygroundSession> => {
    try {
      const response = await apiClient.put<PlaygroundSession>(
        `/api/playground/runs/${sessionId}/restart`,
        data
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'playground session');
    }
  },

  /**
   * Rename a playground session
   */
  renameSession: async (sessionId: string, newName: string): Promise<void> => {
    try {
      await apiClient.patch(`/api/sessions/${sessionId}`, {
        custom_name: newName
      });
    } catch (error) {
      throw handleApiError(error, 'playground session');
    }
  },

  /**
   * Delete a playground session
   */
  deleteSession: async (sessionId: string): Promise<void> => {
    try {
      await apiClient.delete(`/api/sessions/${sessionId}`);
    } catch (error) {
      throw handleApiError(error, 'playground session');
    }
  },

  /**
   * Stop a running session
   */
  stopSession: async (sessionId: string): Promise<void> => {
    try {
      await apiClient.patch(`/api/sessions/${sessionId}`, {
        abort_task: true
      });
    } catch (error) {
      throw handleApiError(error, 'session stop');
    }
  },

  /**
   * Confirm the next step in step mode (advance by one tool call)
   */
  confirmStep: async (sessionId: string): Promise<void> => {
    try {
      await apiClient.post(`/api/sessions/${sessionId}/confirm`);
    } catch (error) {
      throw handleApiError(error, 'step confirm');
    }
  },

  /**
   * Highlight an ABox element in the live browser session
   */
  highlightElement: async (sessionId: string, elementId: number): Promise<void> => {
    try {
      await apiClient.post(`/api/sessions/${sessionId}/highlight`, { element_id: elementId });
    } catch (error) {
      throw handleApiError(error, 'highlight');
    }
  },

  /**
   * Clear any active element highlight
   */
  clearHighlight: async (sessionId: string): Promise<void> => {
    try {
      await apiClient.post(`/api/sessions/${sessionId}/clear-highlight`);
    } catch (error) {
      throw handleApiError(error, 'clear highlight');
    }
  },

  /**
   * Get full page HTML with data-abox-id markers on interactive elements
   */
  getPageHTML: async (sessionId: string): Promise<string> => {
    try {
      const response = await apiClient.get<{ html: string }>(`/api/sessions/${sessionId}/html`);
      return response.data.html || '';
    } catch (error) {
      throw handleApiError(error, 'page HTML');
    }
  },

  /**
   * Get stored HTML snapshot for a specific step
   */
  getStepHTML: async (sessionId: string, stepNumber: number): Promise<string> => {
    try {
      const response = await apiClient.get<{ html: string }>(`/api/sessions/${sessionId}/steps/${stepNumber}/html`);
      return response.data.html || '';
    } catch (error) {
      throw handleApiError(error, 'step HTML');
    }
  },

  /**
   * Get SSE stream URL for a playground session (uses existing session endpoint)
   */
  getStreamUrl: (sessionId: string): string => {
    const token = localStorage.getItem('access_token');
    return `${API_BASE_URL}/api/sessions/${sessionId}/stream${token ? `?token=${token}` : ''}`;
  },

  /**
   * List saved step sequences
   */
  getStepSequences: async (): Promise<{ sequences: StepSequence[] }> => {
    try {
      const response = await apiClient.get<{ sequences: StepSequence[] }>(
        `/api/playground/step-sequences`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'step sequences');
    }
  },

  /**
   * Get a specific step sequence with full steps
   */
  getStepSequence: async (sequenceId: string): Promise<StepSequence & { steps: StepSequenceStep[] }> => {
    try {
      const response = await apiClient.get(
        `/api/playground/step-sequences/${sequenceId}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'step sequence');
    }
  },
};

/**
 * Handle API errors and transform them into user-friendly messages
 */
function handleApiError(error: unknown, entityType: string = 'resource'): Error {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<any>;

    // Network or timeout error
    if (!axiosError.response) {
      if (axiosError.code === 'ECONNABORTED') {
        return new Error('Request timed out. Please try again.');
      }
      return new Error('Unable to connect to the server. Please check your connection.');
    }

    // Server returned an error response
    const status = axiosError.response.status;
    const data = axiosError.response.data;

    // Extract error message from response
    const message = data?.detail || data?.message || axiosError.message;

    switch (status) {
      case 400:
        return new Error(`Validation error: ${message}`);
      case 404:
        return new Error(`${entityType.charAt(0).toUpperCase() + entityType.slice(1)} not found.`);
      case 409:
        return new Error(message); // Conflict message from backend
      case 500:
        return new Error('An error occurred on the server. Please try again later.');
      default:
        return new Error(message || 'An unexpected error occurred.');
    }
  }

  // Non-axios error
  return error instanceof Error ? error : new Error('An unexpected error occurred.');
}
